/**
 * Every string the Access & Permissions tab shows (PD-4070), mirroring
 * CreateUserWizard/copy.js so the wording can be reviewed without reading the
 * components — and so the self-modification sentence has one definition. It is
 * used twice: as the tooltip on the disabled role controls, and as the message
 * for the 422 if a request gets past them.
 */

export const PERMISSIONS_SECTION = {
  title: 'Permissions & Discoverability',
  subtitle:
    "Manage permissions and discoverability associated with this user's accounts.",
  applyToAll: 'Apply to All Accounts',
  customPerAccount: 'Custom per Account',
  save: 'Save Changes',
  saving: 'Saving...',
  cancel: 'Cancel',
};

// The single row "Apply to All Accounts" collapses to. Team and account both
// read as one thing, so the table needs no branch for the mode.
export const ALL_TEAMS_ROW = 'All Teams';
export const ALL_ACCOUNTS_ROW = 'All Accounts';

export const BANDS = {
  permissions: 'Permissions',
  discoverability: 'Discoverability',
};

// Keyed by the server's stable option id, falling back to the option's own
// label. That is how "Management Role" becomes the design's "Asset Mgmt." column
// without changing what the wizard shows, and how a role added server-side gets
// a sensible heading before anyone touches this file.
export const COLUMN_TITLES = {
  account: 'Account',
  'fleet-administrator': 'Fleet Admin',
  'report-user': 'Report',
  'management-role': 'Asset Mgmt.',
  tier: 'Mgmt. Role',
  visibleAsExternalRecipient: 'External',
  visibleAsInternalRecipient: 'Internal',
};

// Positive, because the columns are: a ticked box means the user IS shown.
// The legacy form and the Create User wizard ask the opposite question ("Hide
// from…"), so these say plainly which way round this table reads.
export const COLUMN_INFO = {
  visibleAsExternalRecipient:
    'When checked, this user appears to external users (provider contacts) in the recipients lists for the Notes sections of a case.',
  visibleAsInternalRecipient:
    'When checked, this user appears to internal users and contacts (at my location) in the recipients lists for the Notes sections of a case.',
};

export const LOCKS = {
  // The exact sentence the ticket specifies, and the same one the 422 maps to.
  selfModify: 'You cannot modify your own roles.',
  // Names the way out rather than only refusing: removing the last role is the
  // account-removal flow (PD-4069).
  lastRole:
    'This is the last permission on this account. Use REMOVE ACCESS to remove the user from this account.',
};

export const NOTICES = {
  // Switching to Apply to All when the accounts disagree cannot preserve all of
  // them, so the row opens blank and says so — which also fails validation and
  // keeps SAVE CHANGES disabled until the admin chooses.
  overwrite:
    'These accounts currently have different permissions. Choosing permissions here will apply them to every account.',
  permissionsRequired:
    'Each account needs at least one permission. Accounts with none are highlighted below.',
};

export const MESSAGES = {
  saved: 'Access and permissions updated.',
  failed: 'Access and permissions not updated.',
  generic: 'This account could not be updated.',
};

export const ADD_TO_ACCOUNT = {
  title: 'Add User to Account',
  subtitle: 'Add a user and grant them access to an account.',
  team: 'Team',
  teamPlaceholder: 'All Teams',
  teamHelp: 'Choose one team from the list.',
  accountLabel: 'Account',
  accountHelp:
    'Choose one or multiple accounts from the list, or type to search by name.',
  accountSearchPlaceholder: 'Search accounts by name',
  accountToggle: 'Show account options',
  accountLoadingMore: 'Loading more accounts...',
  accountEmpty: 'No accounts available.',
  accountPaged: (loaded, total) => `Showing ${loaded} of ${total} accounts`,
  submit: 'Add User',
  submitting: 'Adding...',
  cancel: 'Cancel',
  failedTitle: 'Not added.',
};
